import { useParams, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Play, Pause, Ban, Send, XCircle, SkipForward, Users } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { StatCard } from '@/components/shared/stat-card'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { toast } from 'sonner'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { useMyPermissions } from '@/hooks/use-settings'
import { ProgressBar } from './progress-bar'
import {
  useZnsCampaign,
  useStartZnsCampaign,
  useCancelZnsCampaign,
  usePauseZnsCampaign,
  useResumeZnsCampaign,
  useZnsCampaignReport,
  useReconcileZnsCampaign,
  campaignStatusLabel,
  campaignStatusVariant,
  recipientStatusLabel,
  recipientStatusVariant,
  type ZnsCampaignRecipient,
} from '@/hooks/use-zns'

export function ZnsCampaignDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation('marketing')
  const { data: campaign, isLoading, isError } = useZnsCampaign(id)

  const startMutation = useStartZnsCampaign()
  const cancelMutation = useCancelZnsCampaign()
  const pauseMutation = usePauseZnsCampaign()
  const resumeMutation = useResumeZnsCampaign()
  const reconcileMutation = useReconcileZnsCampaign()
  const report = useZnsCampaignReport(id, ['running', 'paused'].includes(campaign?.status ?? ''))
  // Bắt đầu / Tạm dừng / Tiếp tục / Hủy / Đối soát → BE đòi zns.send → không có thì ẩn hẳn (BRD §5.3.1).
  const { data: perms } = useMyPermissions()
  const canSend = !!perms?.has('zns.send')

  if (isLoading) return <Loading label={t('znsDetail.loading')} />
  if (isError || !campaign) return <ErrorState message={t('znsDetail.loadError')} />

  const done = campaign.sentCount + campaign.failedCount + campaign.skippedCount
  const canStart = canSend && (campaign.status === 'draft' || campaign.status === 'queued' || campaign.status === 'scheduled')
  const canCancel = canSend && ['running', 'queued', 'scheduled', 'paused'].includes(campaign.status)

  async function handleStart() {
    try {
      const res = await startMutation.mutateAsync(campaign!.id)
      toast.success(t('znsCampaigns.startedToast', { enqueued: res.enqueued, skipped: res.skipped }))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handlePauseResume() {
    try {
      if (campaign!.status === 'paused') {
        await resumeMutation.mutateAsync(campaign!.id)
        toast.success(t('znsDetail.resumedToast'))
      } else {
        await pauseMutation.mutateAsync(campaign!.id)
        toast.success(t('znsDetail.pausedToast'))
      }
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleReconcile() {
    try {
      const r = await reconcileMutation.mutateAsync(campaign!.id)
      toast.success(t('znsDetail.reconciledToast', { checked: r.checked, nowDelivered: r.nowDelivered }))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function handleCancel() {
    try {
      await cancelMutation.mutateAsync(campaign!.id)
      toast.success(t('znsCampaigns.cancelledToast'))
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const columns: Column<ZnsCampaignRecipient>[] = [
    {
      key: 'phone',
      header: t('znsDetail.columns.phone'),
      cell: (r) => <span className="tabular-nums">{r.phone}</span>,
    },
    {
      key: 'status',
      header: t('znsDetail.columns.status'),
      cell: (r) => <Badge variant={recipientStatusVariant(r.status)}>{recipientStatusLabel(r.status)}</Badge>,
    },
    {
      key: 'error',
      header: t('znsDetail.columns.error'),
      cell: (r) => (
        <span className="text-xs text-muted-foreground">
          {r.errorMessage || '—'}
          {r.errorCode && r.errorCode !== 'missing_param' ? ` (${t('znsDetail.errorCode', { code: r.errorCode })})` : ''}
        </span>
      ),
    },
    {
      key: 'processedAt',
      header: t('znsDetail.columns.processedAt'),
      cell: (r) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {r.processedAt ? dayjs(r.processedAt).format('DD/MM/YYYY HH:mm') : '—'}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title={campaign.name}
        description={`${campaign.channelAccount?.displayName || t('znsCampaigns.oaFallback')} · ${t('znsDetail.templateLabel', { id: campaign.templateId })}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/zns-campaigns')}>
              <ArrowLeft className="h-4 w-4" /> {t('znsDetail.backToList')}
            </Button>
            {canStart && (
              <Button onClick={handleStart} disabled={startMutation.isPending}>
                <Play className="h-4 w-4" /> {t('znsCampaigns.start')}
              </Button>
            )}
            {canSend && (campaign.status === 'running' || campaign.status === 'paused') && (
              <Button
                variant="outline"
                onClick={handlePauseResume}
                disabled={pauseMutation.isPending || resumeMutation.isPending}
              >
                {campaign.status === 'paused' ? (
                  <><Play className="h-4 w-4" /> {t('znsDetail.resume')}</>
                ) : (
                  <><Pause className="h-4 w-4" /> {t('znsDetail.pause')}</>
                )}
              </Button>
            )}
            {canCancel && (
              <Button variant="outline" onClick={handleCancel} disabled={cancelMutation.isPending}>
                <Ban className="h-4 w-4" /> {t('znsCampaigns.cancel')}
              </Button>
            )}
          </div>
        }
      />

      <Card>
        <CardContent className="space-y-3 p-5">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant={campaignStatusVariant(campaign.status)}>
              {campaignStatusLabel(campaign.status)}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {t('znsDetail.createdAt', { date: dayjs(campaign.createdAt).format('DD/MM/YYYY HH:mm') })}
              {campaign.scheduledAt &&
                ` · ${t('znsDetail.scheduledAtInline', { date: dayjs(campaign.scheduledAt).format('DD/MM/YYYY HH:mm') })}`}
              {campaign.startedAt &&
                ` · ${t('znsDetail.startedAtInline', { date: dayjs(campaign.startedAt).format('DD/MM/YYYY HH:mm') })}`}
            </span>
          </div>
          {campaign.status === 'paused' && campaign.pauseReason && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              {t('znsDetail.pausedReason', { reason: campaign.pauseReason })}
            </p>
          )}
          {campaign.windowStart != null && campaign.windowEnd != null && (
            <p className="text-xs text-muted-foreground">
              {t('znsDetail.windowHint', { start: campaign.windowStart, end: campaign.windowEnd })}
            </p>
          )}
          <ProgressBar
            value={done}
            total={campaign.totalCount}
            tone={
              campaign.failedCount > 0
                ? 'warning'
                : campaign.status === 'completed'
                  ? 'success'
                  : 'primary'
            }
          />
          <p className="text-xs text-muted-foreground tabular-nums">
            {t('znsDetail.processed', { done, total: campaign.totalCount })}
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label={t('znsDetail.stats.totalRecipients')} value={campaign.totalCount} icon={Users} />
        <StatCard label={t('znsDetail.stats.sent')} value={campaign.sentCount} icon={Send} tone="success" />
        <StatCard label={t('znsDetail.stats.failed')} value={campaign.failedCount} icon={XCircle} tone="destructive" />
        <StatCard label={t('znsDetail.stats.skipped')} value={campaign.skippedCount} icon={SkipForward} tone="warning" />
      </div>

      {report.data && (
        <Card>
          <CardContent className="space-y-3 p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">{t('znsDetail.reportTitle')}</h2>
              {canSend && (
                <Button variant="outline" size="sm" onClick={handleReconcile} disabled={reconcileMutation.isPending}>
                  {reconcileMutation.isPending ? t('znsDetail.reconciling') : t('znsDetail.reconcileButton')}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard label={t('znsDetail.stats.accepted')} value={report.data.accepted} icon={Send} />
              <StatCard
                label={t('znsDetail.stats.delivered')}
                value={
                  report.data.deliveryRate == null
                    ? '—'
                    : `${report.data.delivered} (${Math.round(report.data.deliveryRate * 100)}%)`
                }
                icon={Users}
                tone="success"
              />
              <StatCard label={t('znsDetail.stats.awaitingDelivery')} value={report.data.awaitingDelivery} icon={SkipForward} tone="warning" />
              <StatCard
                label={t('znsDetail.stats.estimatedCost')}
                value={report.data.estimatedCost == null ? '—' : `${Math.round(report.data.estimatedCost).toLocaleString(currentLocale())}đ`}
                icon={Send}
              />
            </div>
            {report.data.errors.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium">{t('znsDetail.errorsByCode')}</p>
                <ul className="divide-y rounded-md border text-sm">
                  {report.data.errors.map((e) => (
                    <li key={e.code} className="flex items-center justify-between gap-3 px-3 py-1.5">
                      <span className="min-w-0">
                        <span className="font-mono text-xs text-muted-foreground">{e.code}</span> {e.message}
                      </span>
                      <span className="shrink-0 tabular-nums font-medium">{e.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {t('znsDetail.reportHint')}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        <h2 className="text-sm font-semibold">{t('znsDetail.recipientsTitle')}</h2>
        <DataTable
          columns={columns}
          rows={campaign.recipients ?? []}
          rowKey={(r) => r.id}
          emptyTitle={t('znsDetail.recipientsEmpty')}
        />
      </div>
    </div>
  )
}
